import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save } from 'lucide-react'
import { api } from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'
import ImageUpload from '../components/ImageUpload'
import FieldEditor from '../components/FieldEditor'

export default function EditBlockType() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    creator: '',
    icon: '',
    preview_image: '',
    schema: { fields: [] },
    component_name: '',
    is_active: true,
    is_fixed: false,
    fixed_entity_types: [],
    fixed_include_categories: [],
    fixed_exclude_categories: [],
    sort_order: 0,
    locale: 'en',
  })

  const { data: blockTypeRes, isLoading } = useQuery({
    queryKey: ['block-type', id],
    queryFn: () => api.get(`/block-types/${id}`).then((res) => res.data),
    enabled: !!id,
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: async () => {
      const res = await api.get('/categories', { params: { per_page: 100 } })
      return res.data.data || []
    },
  })

  const updateBlockTypeMutation = useMutation({
    mutationFn: (data) => api.put(`/block-types/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['block-types'])
      queryClient.invalidateQueries(['block-type', id])
      alert('Block type updated successfully!')
    },
    onError: (error) => {
      // Error handling
      alert(
        'Failed to update block type. Please check the console for details.'
      )
    },
  })

  useEffect(() => {
    if (blockTypeRes?.data) {
      const blockType = blockTypeRes.data
      setFormData({
        name: blockType.name || '',
        slug: blockType.slug || '',
        description: blockType.description || '',
        creator: blockType.creator || '',
        icon: blockType.icon || '',
        preview_image: blockType.preview_image || '',
        schema: blockType.schema || { fields: [] },
        component_name: blockType.component_name || '',
        is_active: blockType.is_active ?? true,
        is_fixed: blockType.is_fixed ?? false,
        fixed_entity_types: blockType.fixed_entity_types || [],
        fixed_include_categories: blockType.fixed_include_categories || [],
        fixed_exclude_categories: blockType.fixed_exclude_categories || [],
        sort_order: blockType.sort_order || 0,
        locale: 'en',
      })
    }
  }, [blockTypeRes])

  const handleSubmit = (e) => {
    e.preventDefault()

    const data = {
      ...formData,
      fixed_entity_types: formData.is_fixed && formData.fixed_entity_types.length > 0 
        ? formData.fixed_entity_types 
        : null,
      fixed_include_categories: formData.is_fixed && formData.fixed_include_categories.length > 0 
        ? formData.fixed_include_categories 
        : null,
      fixed_exclude_categories: formData.is_fixed && formData.fixed_exclude_categories.length > 0 
        ? formData.fixed_exclude_categories 
        : null,
    }

    updateBlockTypeMutation.mutate(data)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.type !== 'submit' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault()
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSchemaChange = (schema) => {
    setFormData((prev) => ({ ...prev, schema }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="secondary" onClick={() => navigate('/block-types')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Block Type</h1>
          <p className="mt-1 text-sm text-gray-500">
            Modify block type configuration and schema.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="bg-white shadow rounded-lg p-6 space-y-6"
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Block type name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Slug *
            </label>
            <Input
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              placeholder="block-type-slug"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="Block type description"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Creator
            </label>
            <Input
              value={formData.creator}
              onChange={(e) => handleInputChange('creator', e.target.value)}
              placeholder="Creator name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Component Name
            </label>
            <Input
              value={formData.component_name}
              onChange={(e) =>
                handleInputChange('component_name', e.target.value)
              }
              placeholder="ComponentName"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Icon
            </label>
            <Input
              value={formData.icon}
              onChange={(e) => handleInputChange('icon', e.target.value)}
              placeholder="icon-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview Image URL
            </label>
            <ImageUpload
              value={formData.preview_image}
              onChange={(url) => handleInputChange('preview_image', url)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Sort Order
            </label>
            <Input
              type="number"
              value={formData.sort_order}
              onChange={(e) =>
                handleInputChange('sort_order', parseInt(e.target.value) || 0)
              }
            />
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  handleInputChange('is_active', e.target.checked)
                }
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_fixed}
                onChange={(e) => {
                  handleInputChange('is_fixed', e.target.checked)
                  if (!e.target.checked) {
                    handleInputChange('fixed_entity_types', [])
                    handleInputChange('fixed_include_categories', [])
                    handleInputChange('fixed_exclude_categories', [])
                  }
                }}
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">Fixed Block</span>
            </label>
          </div>
        </div>

        {formData.is_fixed && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apply to Entity Types
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.fixed_entity_types?.includes('page')}
                    onChange={(e) => {
                      const current = formData.fixed_entity_types || []
                      if (e.target.checked) {
                        handleInputChange('fixed_entity_types', [...current, 'page'])
                      } else {
                        handleInputChange('fixed_entity_types', current.filter(t => t !== 'page'))
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Pages</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.fixed_entity_types?.includes('post')}
                    onChange={(e) => {
                      const current = formData.fixed_entity_types || []
                      if (e.target.checked) {
                        handleInputChange('fixed_entity_types', [...current, 'post'])
                      } else {
                        handleInputChange('fixed_entity_types', current.filter(t => t !== 'post'))
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Posts</span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Leave empty to apply to all entity types
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Include Categories (only)
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleInputChange('fixed_include_categories', categories.map(c => c.id))}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange('fixed_include_categories', [])}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <select
                  multiple
                  value={(formData.fixed_include_categories || []).map(String)}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => Number(option.value))
                    handleInputChange('fixed_include_categories', selected)
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                  size={4}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name || cat.slug}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Only apply to these categories
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Exclude Categories
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleInputChange('fixed_exclude_categories', categories.map(c => c.id))}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange('fixed_exclude_categories', [])}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <select
                  multiple
                  value={(formData.fixed_exclude_categories || []).map(String)}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => Number(option.value))
                    handleInputChange('fixed_exclude_categories', selected)
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                  size={4}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name || cat.slug}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Never apply to these categories
                </p>
              </div>
            </div>
          </div>
        )}

        <div>
          <FieldEditor schema={formData.schema} onChange={handleSchemaChange} />
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/block-types')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={updateBlockTypeMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {updateBlockTypeMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}
